/*
 * Minimize imports in this entrypoint to avoid increasing the product bundle size.
 */
import { fg } from '@atlaskit/platform-feature-flags';
import { getMutuallyExclusiveUIExperiment } from '@atlassian/search-experiment';

const JIRA_PRODUCT_NAME = 'jira';

// This is referencing KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC from @atlassian/search-client package
// Do not import @atlassian/search-client package as it will increase the NIN bundle size. A unit test is associated to this reference.
// Update DEFAULT_MODEL_ID if the default model ID for intent detection changes
const DEFAULT_MODEL_ID = 'GNIN_HEURISTIC';

const navigationCtaVariants = ['control', 'tabs', 'button', 'icons'] as const;
type NavigationCtaVariant = (typeof navigationCtaVariants)[number];
export const navigationCTACohortValidator = (
	cohortValue: unknown,
): cohortValue is NavigationCtaVariant => {
	return Boolean(navigationCtaVariants.find((variant) => cohortValue === variant));
};

export const suggestedAndRecentQueryLimitValidator = (
	limitValue: unknown,
): limitValue is 0 | 1 | 2 => {
	return typeof limitValue === 'number' && limitValue >= 0 && limitValue <= 2;
};

export const hideFilterSuggestionsValidator = (hideValue: unknown): hideValue is boolean => {
	return typeof hideValue === 'boolean';
};

/**
 * A centralised util to check if the Jira NIN FPS navigation experiment is enabled
 */
export const serveJiraNinFpsNavigation = ({
	primaryProduct,
	fireLayerExposure = true,
}: {
	fireLayerExposure?: boolean;
	primaryProduct?: string | null;
} = {}) => {
	if (primaryProduct !== JIRA_PRODUCT_NAME) {
		return undefined;
	}

	// cohortForIntentDetectionExperiment AKA Experiment 2
	const cohortForIntentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false, // Exposure should only be fired when intent detection has occured on Quick Find
	})?.uiFeatures.navigationCTA;

	// Intent detection experiment takes priority on what CTA is used.
	// For example: If the Jira NIN FPS navigation experiment is decided and we start experiment 2, we still want to be able to set
	// different navigation CTAs in case we want to test different CTA variants in experiment 2.
	if (cohortForIntentDetectionExperiment) {
		return cohortForIntentDetectionExperiment;
	}

	// This gate is used to ensure that certain audiences (i.e. CentralAI) are included in the Jira UI experiment.
	// Without it, some users might be excluded due to layer allocation opting out potential candidates.
	// The gate guarantees participation of the targeted audience during the initial experimentation phase.
	if (!fg('rovo_jira_ui_experience_audience_gate')) {
		return undefined;
	}

	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_jira_nin_fps_navigation_exp',
		'rovo_jira_ui_experience_layer',
		{ fireLayerExposure },
	);

	// cohortForNinFpsExperiment AKA Experiment 1
	const cohortForNinFpsExperiment = layer?.get('cohort', undefined, navigationCTACohortValidator);
	return cohortForNinFpsExperiment;
};

type ServeJiraNINFpsNLQNavigationProps = {
	fireLayerExposure?: boolean;
	primaryProduct?: string | null;
};

const extractNLQNavigationLayerParams = (
	layer: ReturnType<typeof getMutuallyExclusiveUIExperiment>,
) => {
	const variants = ['control', 'test'] as const;
	const cohort = layer?.get(
		'cohort',
		undefined,
		(cohortValue: unknown): cohortValue is (typeof variants)[number] => {
			return Boolean(variants.find((variant) => cohortValue === variant));
		},
	);

	const uiFeatures =
		layer?.get(
			'uiFeatures',
			{},
			(
				uiFeaturesValue: unknown,
			): uiFeaturesValue is {
				navigationCTA?: NavigationCtaVariant;
				suggestedAndRecentQueryLimit?: 0 | 1 | 2;
				hideFilterSuggestions?: boolean;
			} => {
				if (!uiFeaturesValue || typeof uiFeaturesValue !== 'object') {
					return false;
				}

				const isNavigationCTAValid =
					'navigationCTA' in uiFeaturesValue
						? navigationCTACohortValidator(uiFeaturesValue.navigationCTA)
						: true; // Undefined values are valid

				const isLimitSuggestedQueryAndRecentSearchValid =
					'suggestedAndRecentQueryLimit' in uiFeaturesValue
						? suggestedAndRecentQueryLimitValidator(uiFeaturesValue.suggestedAndRecentQueryLimit)
						: true; // Undefined values are valid

				const isHideFilterSuggestionsValid =
					'hideFilterSuggestions' in uiFeaturesValue
						? hideFilterSuggestionsValidator(uiFeaturesValue.hideFilterSuggestions)
						: true; // Undefined values are valid

				return (
					isNavigationCTAValid &&
					isLimitSuggestedQueryAndRecentSearchValid &&
					isHideFilterSuggestionsValid
				);
			},
		) ?? {};

	const modelId =
		layer?.get(
			'modelId',
			DEFAULT_MODEL_ID,
			(modelIdValue: unknown): modelIdValue is string =>
				typeof modelIdValue === 'string' && modelIdValue.length > 0,
		) ?? DEFAULT_MODEL_ID;

	return {
		cohort,
		uiFeatures,
		modelId,
	};
};

const serveJiraNINFpsNLQNavigationUserBased = ({
	primaryProduct,
	fireLayerExposure = true,
}: ServeJiraNINFpsNLQNavigationProps = {}) => {
	if (primaryProduct !== JIRA_PRODUCT_NAME) {
		return undefined;
	}

	// This gate is used to ensure that certain audiences (i.e. CentralAI) are included in the Jira UI experiment.
	// Without it, some users might be excluded due to layer allocation opting out potential candidates.
	// The gate guarantees participation of the targeted audience during the initial experimentation phase.
	if (!fg('rovo_jira_intent_detection_audience_gate')) {
		return undefined;
	}

	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_jira_nin_fps_nlq_navigation_exp',
		'rovo_jira_ui_experience_layer',
		{ fireLayerExposure },
	);
	return extractNLQNavigationLayerParams(layer);
};

const serveJiraNINFpsNLQNavigationTenantBased = ({
	primaryProduct,
	fireLayerExposure = true,
}: ServeJiraNINFpsNLQNavigationProps = {}) => {
	if (primaryProduct !== JIRA_PRODUCT_NAME) {
		return undefined;
	}

	// This gate is used to ensure that certain tenants (i.e. CentralAI) are included in the Jira UI experiment.
	// We use this gate to start the experiment with fixed list of the tenants and do the rest of the rollout percentage-based
	if (!fg('rovo_jira_intent_detection_tenants_gate')) {
		return undefined;
	}

	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_jira_nin_fps_nlq_navigation_tenants_exp',
		'rovo_jira_ui_tenants_experience_layer',
		{ fireLayerExposure },
	);

	return extractNLQNavigationLayerParams(layer);
};

/**
 * A centralised util to check if the Jira NIN FPS Intent detection experiment is enabled
 */
export const serveJiraNINFpsNLQNavigation = (props: ServeJiraNINFpsNLQNavigationProps = {}) =>
	serveJiraNINFpsNLQNavigationTenantBased(props) || serveJiraNINFpsNLQNavigationUserBased(props);
